import React from 'react';
import {
    Grid, Card, CardContent, Typography, TextField, FormControl,
    InputLabel, Select, MenuItem, Button, Stack, Table, TableHead,
    TableRow, TableCell, TableBody, Box, Paper, useTheme
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';

const TreasuryBalanceTab = ({ totalBalance, isKassenwart, entryForm, setEntryForm, handleCreateEntry, entries }) => {
    const theme = useTheme();

    return (
        <Grid container spacing={3} sx={{ width: "100%" }}>
            {/* Balance & Actions */}
            <Grid sx={{ width: "100%" }}>
                <Stack spacing={3}>
                    <Paper
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            p: 3,
                            borderRadius: "12px",
                            bgcolor: 'primary.main',
                            color: '#000',
                            boxShadow: '0 20px 40px rgba(190, 242, 100, 0.2)'
                        }}
                    >
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, textTransform: 'uppercase', opacity: 0.7, mb: 1 }}>
                                Aktueller Stand
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 900, fontFamily: '"Fira Code", monospace' }}>
                                {totalBalance.toFixed(2)} €
                            </Typography>
                        </Box>
                    </Paper>

                    {isKassenwart && (
                        <Paper
                            sx={{
                                width: "100%",
                                p: 3,
                                borderRadius: "12px",
                                border: '1px solid',
                                borderColor: 'divider',
                                background: 'rgba(255,255,255,0.02)'
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 800, mb: 3 }}>
                                Neuer Eintrag
                            </Typography>
                            <form onSubmit={handleCreateEntry}>
                                <Stack spacing={2.5}>
                                    <TextField
                                        label="Beschreibung"
                                        required
                                        fullWidth
                                        value={entryForm.beschreibung}
                                        onChange={e => setEntryForm({ ...entryForm, beschreibung: e.target.value })}
                                    />
                                    <TextField
                                        label="Betrag (€)"
                                        type="number"
                                        inputProps={{ step: "0.01" }}
                                        required
                                        fullWidth
                                        value={entryForm.betrag}
                                        onChange={e => setEntryForm({ ...entryForm, betrag: e.target.value })}
                                    />
                                    <FormControl fullWidth>
                                        <InputLabel>Typ</InputLabel>
                                        <Select
                                            value={entryForm.typ}
                                            label="Typ"
                                            onChange={e => setEntryForm({ ...entryForm, typ: e.target.value })}
                                        >
                                            <MenuItem value="EINNAHME">Einnahme (+)</MenuItem>
                                            <MenuItem value="AUSGABE">Ausgabe (-)</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        type="date"
                                        label="Datum"
                                        InputLabelProps={{ shrink: true }}
                                        required
                                        fullWidth
                                        value={entryForm.datum}
                                        onChange={e => setEntryForm({ ...entryForm, datum: e.target.value })}
                                    />
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        fullWidth
                                        startIcon={<AddRoundedIcon />}
                                        sx={{ py: 1.5, fontWeight: 900 }}
                                    >
                                        Eintragen
                                    </Button>
                                </Stack>
                            </form>
                        </Paper>
                    )}
                </Stack>
            </Grid>

            {/* Transactions */}
            <Grid sx={{ width: "100%" }}>
                <Paper
                    sx={{
                        p: 0,
                        borderRadius: "12px",
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden'
                    }}
                >
                    <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            Verlauf
                        </Typography>
                    </Box>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table>
                            <TableHead sx={{ bgcolor: 'rgba(255,255,255,0.02)' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary' }}>Datum</TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary' }}>Beschreibung</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 800, color: 'text.secondary' }}>Betrag</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {entries.map((e, idx) => (
                                    <TableRow
                                        key={e.id}
                                        sx={{
                                            transition: 'all 0.2s',
                                            '&:hover': { bgcolor: 'rgba(190, 242, 100, 0.05)' }
                                        }}
                                    >
                                        <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                            {new Date(e.datum).toLocaleDateString('de-DE')}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{e.beschreibung}</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 800, color: e.typ === 'EINNAHME' ? 'primary.main' : 'error.main', textTransform: 'uppercase', fontSize: '0.6rem' }}>
                                                {e.typ}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                                                {e.typ === 'EINNAHME' ? <TrendingUpRoundedIcon sx={{ fontSize: 16, color: 'primary.main' }} /> : <TrendingDownRoundedIcon sx={{ fontSize: 16, color: 'error.main' }} />}
                                                <Typography sx={{ fontWeight: 900, fontFamily: '"Fira Code", monospace', color: e.typ === 'EINNAHME' ? 'primary.main' : 'error.main' }}>
                                                    {e.typ === 'EINNAHME' ? '+' : '-'}{parseFloat(e.betrag).toFixed(2)} €
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {entries.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center" sx={{ py: 8, color: 'text.disabled', fontWeight: 600 }}>
                                            Kein Verlauf gefunden
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default TreasuryBalanceTab;
